import React, { useState } from 'react';
import { Download, Eye } from 'lucide-react';
import { Attachment } from '../models/Content';
import { formatFileSize, getFileIcon, downloadAttachment, canPreviewFile } from '../utils/fileUtils';
import FilePreviewModal from './FilePreviewModal';

interface AttachmentsDisplayProps {
  attachments: Attachment[];
  title?: string;
  className?: string;
}

const AttachmentsDisplay: React.FC<AttachmentsDisplayProps> = ({
  attachments,
  title = "Attachments",
  className = ""
}) => {
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const handlePreview = (attachment: Attachment) => {
    setPreviewAttachment(attachment);
  };

  const closePreview = () => {
    setPreviewAttachment(null);
  };

  return (
    <>
      <div className={className}>
        <div className="space-y-3">
          {attachments.map((attachment) => (
            <div 
              key={attachment.id} 
              className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all"
            >
              <div className="flex-shrink-0 text-slate-600">
                {getFileIcon(attachment.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {attachment.name}
                </div>
                <div className="text-xs text-slate-500">
                  {formatFileSize(attachment.size)}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {canPreviewFile(attachment.type) && (
                  <button
                    onClick={() => handlePreview(attachment)}
                    className="flex-shrink-0 p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                    title="Preview file"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => downloadAttachment(attachment)}
                  className="flex-shrink-0 p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  title="Download file"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {previewAttachment && (
        <FilePreviewModal
          attachment={previewAttachment}
          isOpen={!!previewAttachment}
          onClose={closePreview}
        />
      )}
    </>
  );
};

export default AttachmentsDisplay; 